import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, ArrowUpRight, Sparkles, RotateCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import {
  T, FONT_DISPLAY, FONT_MONO, PageShell, TopNav, Footer,
  container, eyebrow, italicAccent, btnCoral,
} from "@/lib/hybridTheme";

/**
 * Public "you paid, welcome aboard" page. Reached via Stripe Checkout success
 * redirect. Polls /api/public/payment-status until it flips to "paid".
 */
export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setError("Missing session ID."); return; }
    let cancelled = false;
    let timer;
    async function poll(attempt = 0) {
      try {
        const { data } = await api.get(`/public/payment-status/${sessionId}`);
        if (cancelled) return;
        setInfo(data);
        setAttempts(attempt + 1);
        if (data.payment_status !== "paid" && attempt < 12) {
          timer = setTimeout(() => poll(attempt + 1), 2000);
        }
      } catch (err) {
        if (!cancelled) setError(formatApiError(err));
      }
    }
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [sessionId]);

  const paid = info?.payment_status === "paid";

  return (
    <PageShell testId="checkout-success-page">
      <TopNav />

      <section style={{ padding: "120px 0 96px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -80, right: -100, width: 600, height: 600, background: `radial-gradient(circle, rgba(107,142,90,0.10) 0%, transparent 60%)`, pointerEvents: "none", filter: "blur(50px)" }} />
        <div style={container()}>
          <div style={{ maxWidth: 780, margin: "0 auto", textAlign: "center", position: "relative" }}>
            {paid ? (
              <>
                <div style={{ width: 88, height: 88, borderRadius: "50%", background: T.sage, color: T.cream, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 32px" }}>
                  <Check size={44} strokeWidth={3} />
                </div>
                <div style={eyebrow()}>Welcome aboard</div>
                <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(48px, 6vw, 80px)", lineHeight: 1.02, letterSpacing: "-0.03em", fontWeight: 400, margin: "20px 0 24px", color: T.ink }}>
                  You&apos;re in. <em style={italicAccent(T.coral)}>Let&apos;s build.</em>
                </h1>
                <p style={{ fontSize: 19, lineHeight: 1.55, color: T.inkMuted, maxWidth: 620, margin: "0 auto" }}>
                  We just received your payment and pinged our onboarding team. Expect an email at{" "}
                  <strong style={{ color: T.ink }}>{info?.email || "your inbox"}</strong>{" "}
                  within the next business hour with your kickoff-call scheduler link.
                </p>
                {info?.amount && (
                  <div style={{ marginTop: 32, display: "inline-flex", alignItems: "center", gap: 20, padding: "16px 24px", background: T.panel, border: `1px solid ${T.line}` }}>
                    <div>
                      <div style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.14em", textTransform: "uppercase", color: T.inkFaint }}>Charged</div>
                      <div style={{ fontFamily: FONT_DISPLAY, fontSize: 28, color: T.ink }}>${info.amount.toFixed(2)}</div>
                    </div>
                    <div style={{ width: 1, height: 40, background: T.line }} />
                    <div>
                      <div style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.14em", textTransform: "uppercase", color: T.inkFaint }}>Plan</div>
                      <div style={{ fontFamily: FONT_DISPLAY, fontSize: 18, color: T.ink, fontStyle: "italic" }}>{prettyPlan(info.lookup_key)}</div>
                    </div>
                  </div>
                )}
                <div style={{ marginTop: 48, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                  <Link to="/onboarding-public" style={{ ...btnCoral(), padding: "14px 24px", fontSize: 15, textDecoration: "none" }}>
                    See what happens next <ArrowUpRight size={16} />
                  </Link>
                  <Link to="/" style={{ background: "transparent", color: T.ink, border: `1px solid ${T.line}`, padding: "14px 22px", fontSize: 15, fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>
                    Back to home
                  </Link>
                </div>
              </>
            ) : error ? (
              <>
                <div style={eyebrow()}>Hmm</div>
                <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: 60, letterSpacing: "-0.03em", lineHeight: 1.05, fontWeight: 400, margin: "16px 0 16px" }}>
                  We couldn&apos;t confirm your payment.
                </h1>
                <p style={{ fontSize: 17, color: T.inkMuted, lineHeight: 1.6, maxWidth: 560, margin: "0 auto" }}>{error}</p>
                <div style={{ marginTop: 32 }}>
                  <Link to="/pricing-public" style={{ ...btnCoral(), padding: "14px 24px", fontSize: 15, textDecoration: "none" }}>Back to pricing</Link>
                </div>
              </>
            ) : (
              <>
                <RotateCw size={44} className="animate-spin" style={{ color: T.coral, margin: "0 auto 24px" }} />
                <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: 48, letterSpacing: "-0.02em", lineHeight: 1.1, fontWeight: 400, margin: 0 }}>
                  <em style={italicAccent(T.coral)}>Confirming</em> your payment…
                </h1>
                <p style={{ marginTop: 20, color: T.inkMuted, fontSize: 15 }}>
                  {attempts > 0 ? `Attempt ${attempts} · Stripe usually confirms within 10 seconds.` : "One moment."}
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 28px 120px" }}>
        <div style={{ background: T.canvas, border: `1px solid ${T.lineSoft}`, padding: "32px 36px", display: "grid", gridTemplateColumns: "auto 1fr", gap: 24, alignItems: "start" }}>
          <Sparkles size={22} color={T.coral} style={{ marginTop: 4 }} />
          <div>
            <div style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 500, color: T.ink }}>What happens next</div>
            <ul style={{ margin: "12px 0 0", padding: 0, listStyle: "none", fontSize: 14, color: T.inkMuted, lineHeight: 1.7 }}>
              <li>1. You&apos;ll get a welcome email with your onboarding link.</li>
              <li>2. Book a 20-minute discovery call — we map your services + hours.</li>
              <li>3. Days 2-4 we build your AI agent + free preview website.</li>
              <li>4. Day 5 you test it live from your own phone.</li>
              <li>5. Day 6-7 you go live. Your existing number gets forwarded, and the AI starts booking.</li>
            </ul>
          </div>
        </div>
      </div>

      <Footer />
    </PageShell>
  );
}

function prettyPlan(lookup) {
  const map = {
    starter_monthly: "Starter",
    growth_monthly:  "Front Office",
    elite_monthly:   "Multi-Location",
  };
  return map[lookup] || lookup || "—";
}
